import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import RecapViewerModal from './RecapViewerModal';
import './BuildGraph.css';
import './GraphStats.css';

/**
 * GraphStatsPage - Comprehensive graph statistics and quality score view
 *
 * Sections:
 * 1. Quality Score gauge
 * 2. Basic counts (nodes, synapses, gates, orphans)
 * 3. Connectivity summary
 * 4. Node kind distribution
 * 5. Synapse kind distribution
 * 6. Gate analysis
 * 7. Depth stats
 * 8. Chunk processing recap
 */

const GRADE_COLORS = {
  A: '#22c55e',
  B: '#84cc16',
  C: '#eab308',
  D: '#f97316',
  F: '#ef4444',
};

function QualityGauge({ score, grade }) {
  // SVG circular gauge
  const radius = 70;
  const stroke = 12;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = GRADE_COLORS[grade] || '#6b7280';

  return (
    <div className="graph-stats__gauge">
      <svg width="180" height="180" viewBox="0 0 180 180">
        {/* Background circle */}
        <circle
          cx="90" cy="90" r={radius}
          fill="none"
          stroke="var(--uh-border, #e5e7eb)"
          strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx="90" cy="90" r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
        {/* Score text */}
        <text x="90" y="82" textAnchor="middle" className="graph-stats__gauge-score">
          {score}
        </text>
        {/* Grade letter */}
        <text x="90" y="110" textAnchor="middle" className="graph-stats__gauge-grade" fill={color}>
          {grade}
        </text>
      </svg>
      <div className="graph-stats__gauge-label">Graph Quality</div>
    </div>
  );
}

function DistributionBar({ items, maxVal }) {
  if (!items || items.length === 0) return <div className="graph-stats__empty">No data</div>;
  const max = maxVal || Math.max(...items.map(i => i.count));
  return (
    <div className="graph-stats__bar-chart">
      {items.map(({ name, count }) => (
        <div className="graph-stats__bar-row" key={name}>
          <span className="graph-stats__bar-label" title={name}>{name}</span>
          <div className="graph-stats__bar-track">
            <div
              className="graph-stats__bar-fill"
              style={{ width: `${max > 0 ? (count / max) * 100 : 0}%` }}
            />
          </div>
          <span className="graph-stats__bar-value">{count}</span>
        </div>
      ))}
    </div>
  );
}

function FactorBreakdown({ factors }) {
  if (!factors) return null;
  const entries = Object.entries(factors);
  return (
    <div className="graph-stats__factors">
      <h4 className="graph-stats__factors-title">Quality Factor Breakdown</h4>
      <div className="graph-stats__factors-grid">
        {entries.map(([key, f]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const barColor = f.score >= 80 ? '#22c55e' : f.score >= 60 ? '#eab308' : '#ef4444';
          return (
            <div className="graph-stats__factor-item" key={key}>
              <div className="graph-stats__factor-header">
                <span className="graph-stats__factor-name">{label}</span>
                <span className="graph-stats__factor-weight">{(f.weight * 100).toFixed(0)}%</span>
              </div>
              <div className="graph-stats__factor-bar-track">
                <div
                  className="graph-stats__factor-bar-fill"
                  style={{ width: `${f.score}%`, backgroundColor: barColor }}
                />
              </div>
              <div className="graph-stats__factor-detail">
                <span className="graph-stats__factor-score">{f.score.toFixed(1)}/100</span>
                <span className="graph-stats__factor-desc">{f.detail}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GraphStatsPage() {
  const { graphId } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chunkExpanded, setChunkExpanded] = useState(false);
  const [recapModalOpen, setRecapModalOpen] = useState(false);
  const [recapModalChunkIndex, setRecapModalChunkIndex] = useState(0);
  const [vocabStatus, setVocabStatus] = useState('unknown'); // 'unknown' | 'building' | 'cached' | 'error'
  const [vocabBuiltAt, setVocabBuiltAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [domainOntologyStatus, setDomainOntologyStatus] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getGraphStats(graphId);
        if (data.success) {
          setStats(data);
          // Auto-build vocabulary in background after stats load
          buildVocabulary();
        } else {
          setError(data.error || 'Failed to load stats');
        }
      } catch (err) {
        console.error('Error fetching graph stats:', err);
        setError(err.response?.data?.error || err.message || 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphId]);

  const handleRefreshStats = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const data = await apiService.refreshGraphStats(graphId);
      if (data.success) {
        setStats(data);
      } else {
        setRefreshError(data.error || 'Failed to refresh stats');
      }
    } catch (err) {
      console.error('Error refreshing graph stats:', err);
      setRefreshError(err.response?.data?.error || err.message || 'Failed to refresh stats');
    } finally {
      setRefreshing(false);
    }
  };

  const formatTimestamp = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString();
    } catch (_e) {
      return null;
    }
  };

  const buildVocabulary = async () => {
    setVocabStatus('building');
    try {
      const result = await apiService.buildGraphVocabulary(graphId);
      if (result.success) {
        setVocabStatus('cached');
        setVocabBuiltAt(result.vocabulary?.built_at || new Date().toISOString());
      } else {
        setVocabStatus('error');
      }
    } catch (err) {
      console.error('Vocabulary build error:', err);
      setVocabStatus('error');
    }
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu />
        <div className="graph-stats__loading">
          <div className="graph-stats__spinner" />
          <p>Loading graph statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu />
        <div className="graph-stats__error">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const { basic_counts, connectivity, orphan_nodes, gate_stats, depth_stats,
    node_type_distribution, node_kind_distribution, synapse_kind_distribution,
    synapse_type_distribution, domain_type_distribution,
    coverage, chunk_results, quality_score, stats_meta } = stats;

  const refreshedAtLabel = formatTimestamp(stats_meta?.stats_refreshed_at);
  const graphChangedAt = stats_meta?.graph_changed_at ? new Date(stats_meta.graph_changed_at) : null;
  const serCompiledAt = stats_meta?.ser_compiled_at ? new Date(stats_meta.ser_compiled_at) : null;
  const serIsStale =
    !!(graphChangedAt && serCompiledAt && graphChangedAt.getTime() > serCompiledAt.getTime());

  const byCountDesc = (a, b) => b.count - a.count;
  const nodeKindItems = Object.entries(node_kind_distribution || {}).map(([name, count]) => ({ name, count })).sort(byCountDesc);
  const synapseKindItems = Object.entries(synapse_kind_distribution || {}).map(([name, count]) => ({ name, count })).sort(byCountDesc);
  const synapseTypeItems = Object.entries(synapse_type_distribution || {}).map(([name, count]) => ({ name, count })).sort(byCountDesc);
  const nodeTypeItems = Object.entries(node_type_distribution || {}).map(([name, count]) => ({ name, count })).sort(byCountDesc);
  const domainTypeItems = Object.entries(domain_type_distribution || {}).filter(([name]) => name !== 'unclassified').map(([name, count]) => ({ name: name.replace(/_/g, ' '), count })).sort(byCountDesc);

  return (
    <div className="build-graph-page">
      <BuildGraphMenu />

      <div className="graph-stats">
        <section className="graph-stats__section graph-stats__refresh-section">
          <div className="graph-stats__refresh-bar">
            <div className="graph-stats__refresh-text">
              <h2 className="graph-stats__refresh-title">Graph Statistics</h2>
              <div className="graph-stats__refresh-meta">
                {refreshedAtLabel
                  ? `Last refreshed: ${refreshedAtLabel}`
                  : 'Last refreshed: never'}
                {stats_meta?.stats_refreshed_by ? ` (by ${stats_meta.stats_refreshed_by})` : ''}
              </div>
            </div>
            <button
              type="button"
              className="graph-stats__refresh-btn"
              onClick={handleRefreshStats}
              disabled={refreshing}
              title="Recompute stored counts and analytical breakdowns from the current graph"
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          {refreshError && (
            <div className="graph-stats__refresh-error">{refreshError}</div>
          )}
          {serIsStale && (
            <div className="graph-stats__ser-stale">
              Graph nodes/synapses changed after the last SER compile
              {serCompiledAt ? ` (${formatTimestamp(stats_meta.ser_compiled_at)})` : ''}.
              Consider running &quot;Compile with SER (Full)&quot; again to refresh receptors.
            </div>
          )}
        </section>

        {/* Section 1: Quality Score */}
        <section className="graph-stats__section graph-stats__quality-section">
          <div className="graph-stats__vocab-bar">
            <span className={`graph-stats__vocab-badge graph-stats__vocab-badge--${vocabStatus}`}>
              {vocabStatus === 'building' && 'Building vocabulary...'}
              {vocabStatus === 'cached' && `Vocabulary cached${vocabBuiltAt ? ` (${new Date(vocabBuiltAt).toLocaleString()})` : ''}`}
              {vocabStatus === 'error' && 'Vocabulary build failed'}
              {vocabStatus === 'unknown' && 'Vocabulary not built'}
            </span>
            <button
              className="graph-stats__expand-btn"
              onClick={buildVocabulary}
              disabled={vocabStatus === 'building'}
            >
              {vocabStatus === 'building' ? 'Building...' : 'Refresh Vocabulary'}
            </button>
          </div>
          <div className="graph-stats__quality-row">
            <QualityGauge score={quality_score?.overall || 0} grade={quality_score?.grade || 'F'} />
            <FactorBreakdown factors={quality_score?.factors} />
          </div>
        </section>

        {/* Section 2: Basic Counts */}
        <section className="graph-stats__section">
          <h3 className="graph-stats__section-title">Overview</h3>
          <div className="graph-stats__cards-row">
            <div className="graph-stats__card">
              <div className="graph-stats__card-value">{basic_counts?.total_nodes?.toLocaleString() || 0}</div>
              <div className="graph-stats__card-label">Total Nodes</div>
            </div>
            <div className="graph-stats__card">
              <div className="graph-stats__card-value">{basic_counts?.total_synapses?.toLocaleString() || 0}</div>
              <div className="graph-stats__card-label">Total Connections</div>
            </div>
            <div className="graph-stats__card">
              <div className="graph-stats__card-value">{basic_counts?.total_gates?.toLocaleString() || 0}</div>
              <div className="graph-stats__card-label">Gate Nodes</div>
            </div>
            <div className="graph-stats__card graph-stats__card--warning">
              <div className="graph-stats__card-value">{orphan_nodes?.count?.toLocaleString() || 0}</div>
              <div className="graph-stats__card-label">
                Orphan Nodes
                <span className="graph-stats__badge">{orphan_nodes?.percent?.toFixed(1) || 0}%</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Connectivity */}
        <section className="graph-stats__section">
          <h3 className="graph-stats__section-title">Connectivity</h3>
          <div className="graph-stats__cards-row">
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{connectivity?.avg_in_degree?.toFixed(2) || 0}</div>
              <div className="graph-stats__card-label">Avg In-Degree</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{connectivity?.avg_out_degree?.toFixed(2) || 0}</div>
              <div className="graph-stats__card-label">Avg Out-Degree</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{connectivity?.avg_total_degree?.toFixed(2) || 0}</div>
              <div className="graph-stats__card-label">Avg Total Degree</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{connectivity?.max_in_degree || 0}</div>
              <div className="graph-stats__card-label">Max In-Degree</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{connectivity?.max_out_degree || 0}</div>
              <div className="graph-stats__card-label">Max Out-Degree</div>
            </div>
          </div>
        </section>

        {/* Section 4: Coverage & Depth */}
        <section className="graph-stats__section">
          <h3 className="graph-stats__section-title">Coverage &amp; Depth</h3>
          <div className="graph-stats__cards-row">
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{coverage?.overall_percent?.toFixed(1) || 0}%</div>
              <div className="graph-stats__card-label">Overall Coverage</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{coverage?.total_chunks || 0}</div>
              <div className="graph-stats__card-label">Total Chunks</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{coverage?.chunks_below_threshold || 0}</div>
              <div className="graph-stats__card-label">Chunks Below 70%</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{depth_stats?.max_depth || 0}</div>
              <div className="graph-stats__card-label">Max Path Depth</div>
            </div>
            <div className="graph-stats__card graph-stats__card--sm">
              <div className="graph-stats__card-value">{depth_stats?.avg_depth?.toFixed(2) || 0}</div>
              <div className="graph-stats__card-label">Avg Path Depth</div>
            </div>
          </div>
        </section>

        {/* Section 5: Node Type Distribution */}
        <div className="graph-stats__two-col">
          <section className="graph-stats__section">
            <h3 className="graph-stats__section-title">Node Type Distribution</h3>
            <DistributionBar items={nodeTypeItems} />
          </section>

          {/* Section 6: Node Kind Distribution */}
          <section className="graph-stats__section">
            <h3 className="graph-stats__section-title">Node Kind Distribution</h3>
            <DistributionBar items={nodeKindItems} />
          </section>
        </div>

        {/* Domain Type Distribution */}
        <section className="graph-stats__section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <h3 className="graph-stats__section-title" style={{ margin: 0 }}>Domain Type Distribution</h3>
            {domainTypeItems.length === 0 && (
              <button
                className="btn-secondary btn-sm"
                disabled={domainOntologyStatus === 'running'}
                onClick={async () => {
                  setDomainOntologyStatus('running');
                  try {
                    const resp = await apiService.applyDomainOntology(graphId);
                    if (resp.error) {
                      setDomainOntologyStatus(`error: ${resp.error}`);
                    } else {
                      setDomainOntologyStatus(`done: ${resp.classified} nodes classified. Refreshing stats...`);
                      await handleRefreshStats();
                      setDomainOntologyStatus(null);
                    }
                  } catch (err) {
                    setDomainOntologyStatus(`error: ${err.message}`);
                  }
                }}
              >
                {domainOntologyStatus === 'running' ? 'Classifying...' : 'Apply Domain Ontology'}
              </button>
            )}
          </div>
          {domainTypeItems.length > 0 ? (
            <DistributionBar items={domainTypeItems} />
          ) : (
            <p style={{ color: '#6b7280', fontSize: '13px', margin: '4px 0' }}>
              {domainOntologyStatus && domainOntologyStatus !== 'running'
                ? domainOntologyStatus
                : 'No domain types classified yet. Click "Apply Domain Ontology" to classify existing nodes.'}
            </p>
          )}
        </section>

        {/* Section 7: Synapse Kind Distribution */}
        <div className="graph-stats__two-col">
          <section className="graph-stats__section">
            <h3 className="graph-stats__section-title">Connection Kind Distribution</h3>
            <DistributionBar items={synapseKindItems} />
          </section>

          {/* Section 7b: Synapse Type (Edge Name) Distribution */}
          <section className="graph-stats__section">
            <h3 className="graph-stats__section-title">Connection Type Distribution</h3>
            <DistributionBar items={synapseTypeItems} />
          </section>
        </div>

        {/* Section 8: Gate Analysis */}
        <section className="graph-stats__section">
          <h3 className="graph-stats__section-title">Gate Analysis</h3>
          {gate_stats?.total_gates > 0 ? (
            <div className="graph-stats__table-wrap">
              <table className="graph-stats__table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Total Gates</td>
                    <td>{gate_stats.total_gates}</td>
                  </tr>
                  <tr>
                    <td>Fully Wired (inputs &amp; outputs)</td>
                    <td>{gate_stats.fully_wired}</td>
                  </tr>
                  <tr>
                    <td>Missing Inputs</td>
                    <td className={gate_stats.missing_inputs > 0 ? 'graph-stats__cell--warn' : ''}>{gate_stats.missing_inputs}</td>
                  </tr>
                  <tr>
                    <td>Missing Outputs</td>
                    <td className={gate_stats.missing_outputs > 0 ? 'graph-stats__cell--warn' : ''}>{gate_stats.missing_outputs}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="graph-stats__empty">No gate nodes found in this graph.</div>
          )}
        </section>

        {/* Section 9: Chunk Processing Recap */}
        <section className="graph-stats__section">
          <h3 className="graph-stats__section-title">
            Chunk Processing Recap
            {chunk_results && chunk_results.length > 0 && (
              <button
                className="graph-stats__expand-btn"
                onClick={() => setChunkExpanded(!chunkExpanded)}
              >
                {chunkExpanded ? 'Collapse' : `Expand (${chunk_results.length} chunks)`}
              </button>
            )}
          </h3>
          {chunk_results && chunk_results.length > 0 ? (
            <div className={`graph-stats__chunk-table-wrap ${chunkExpanded ? 'expanded' : 'collapsed'}`}>
              <table className="graph-stats__table graph-stats__table--chunks">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nodes</th>
                    <th>Connections</th>
                    <th>Gates</th>
                    <th>Coverage</th>
                    <th>Recap</th>
                    <th>Iterations</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(chunkExpanded ? chunk_results : chunk_results.slice(0, 5)).map((cr, idx) => (
                    <tr key={cr.chunk_id || idx}>
                      <td>{cr.chunk_index ?? idx + 1}</td>
                      <td>{cr.nodes_count}</td>
                      <td>{cr.synapses_count}</td>
                      <td>{cr.gates_count || 0}</td>
                      <td>
                        <span className={`graph-stats__coverage-pill ${cr.coverage_percent >= 70 ? 'good' : 'low'}`}>
                          {cr.coverage_percent?.toFixed(1) || 0}%
                        </span>
                      </td>
                      <td>
                        {cr.recap_injected ? (
                          <button
                            className="graph-stats__link-btn"
                            onClick={() => {
                              setRecapModalChunkIndex(idx);
                              setRecapModalOpen(true);
                            }}
                          >
                            View ({cr.recap_chars ? `${(cr.recap_chars / 1000).toFixed(1)}k` : '?'})
                          </button>
                        ) : (
                          <span style={{ color: '#aaa' }}>—</span>
                        )}
                      </td>
                      <td>{cr.iterations || 1}</td>
                      <td>
                        <span className={`graph-stats__status-dot ${cr.success !== false ? 'success' : 'fail'}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!chunkExpanded && chunk_results.length > 5 && (
                <div className="graph-stats__more-rows">
                  + {chunk_results.length - 5} more chunks
                </div>
              )}
            </div>
          ) : (
            <div className="graph-stats__empty">No chunk processing data available.</div>
          )}
        </section>
      </div>

      {/* Recap Viewer Modal */}
      <RecapViewerModal
        isOpen={recapModalOpen}
        onClose={() => setRecapModalOpen(false)}
        chunkResults={chunk_results || []}
        initialChunkIndex={recapModalChunkIndex}
      />
    </div>
  );
}

export default GraphStatsPage;
